// src/features/user-portfolios/pages/MyPortfoliosPage.tsx
//
// Phase 4 — "My Portfolios" hub. Institutional UX via DECOUPLED FETCHING:
// the hub fetches the lightweight portfolio list instantly, then EACH card
// independently fetches its own heavy KPI metrics (Numba FIFO + Polars) and
// shows a skeleton until they trickle in — so one slow portfolio never blocks
// the page.
//
// Adapted to this codebase:
//   - Wrapped in the shared <Layout sidebar={<Sidebar/>}> shell like every
//     other dashboard page (title/subtitle handled by Layout).
//   - Theme-aware (light + dark) classes to match the app's slate design
//     system, instead of the dark-only mock.
//   - Uses the feature's api.ts helpers (createPortfolio takes a name string).

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { GlobalSearchBar } from '../../global-search';
import { Layout, Sidebar } from '../../../shared/ui';
import {
    createPortfolio,
    deletePortfolio,
    fetchPortfolios,
    getPortfolioDashboard,
    updatePortfolio,
    type CurrencyCode,
    type DashboardMetrics,
    type Portfolio,
} from '../api';
import { computeRebasedPerformance } from '../utils/performance';

const MAX_PORTFOLIOS = 6;

// Native, globally-correct signed money formatter via Intl.NumberFormat so ANY
// base currency renders its proper symbol (CA$, £, MX$, ¥...). Falls back to an
// ISO-prefixed number for exotic/unknown codes instead of throwing.
const formatSignedMoney = (val: number, ccy: string) => {
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: ccy || 'USD',
            signDisplay: 'exceptZero',
        }).format(val || 0);
    } catch {
        return `${val > 0 ? '+' : val < 0 ? '-' : ''}${ccy || 'USD'} ${Math.abs(
            val || 0,
        ).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    }
};

// --- Sub-Component: Independent Portfolio Card ---------------------------
interface PortfolioCardProps {
    portfolio: Portfolio;
    onDelete: (id: number) => void;
    onRefresh: () => void;
}

const PortfolioCard = ({ portfolio, onDelete, onRefresh }: PortfolioCardProps) => {
    const navigate = useNavigate();
    const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
    // YTD portfolio-vs-benchmark %, computed with the SAME shared rebase util as
    // the dashboard's "Return vs {benchmark}" KPI so both screens always agree.
    const [ytdPerf, setYtdPerf] = useState({ portPeriodPct: 0, benchPeriodPct: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState(portfolio.name);
    // Editable reporting base currency (the "lens"), seeded from the portfolio.
    const [editCurrency, setEditCurrency] = useState<CurrencyCode>(
        (portfolio.base_currency as CurrencyCode) || 'USD',
    );

    // Independent, non-blocking KPI fetch — the heavy backend engine runs
    // per-card so the grid renders instantly and fills in as each resolves.
    useEffect(() => {
        let isMounted = true;
        setIsLoading(true);
        getPortfolioDashboard(portfolio.id)
            .then((data) => {
                if (isMounted) {
                    setMetrics(data.metrics);
                    const { portPeriodPct, benchPeriodPct } =
                        computeRebasedPerformance(data.chart_data ?? [], 'YTD');
                    setYtdPerf({ portPeriodPct, benchPeriodPct });
                    setIsLoading(false);
                }
            })
            .catch(() => {
                if (isMounted) setIsLoading(false);
            });
        return () => {
            isMounted = false;
        };
    }, [portfolio.id]);

    // Save name and/or base-currency edits via a single partial PATCH. Only the
    // facets that actually changed are sent, so a rename never resets the
    // currency and a currency flip never touches the name.
    const handleSave = async () => {
        const nextName = editName.trim();
        const hasNameChanged = !!nextName && nextName !== portfolio.name;
        const hasCcyChanged = editCurrency !== portfolio.base_currency;

        if (hasNameChanged || hasCcyChanged) {
            await updatePortfolio(portfolio.id, {
                name: hasNameChanged ? nextName : undefined,
                base_currency: hasCcyChanged ? editCurrency : undefined,
            });
            onRefresh();
        }
        setIsEditing(false);
    };

    const goToDetail = () => navigate(`/dashboard/my-portfolios/${portfolio.id}`);

    const unrealized = metrics?.total_unrealized_currency ?? 0;
    // Match the dashboard's YTD KPI exactly (rebased chart_data, not raw TWR).
    const ytd = ytdPerf.portPeriodPct;
    const benchYtd = ytdPerf.benchPeriodPct;

    return (
        <div className="group relative flex flex-col rounded-xl border border-slate-200 dark:border-slate-700 hover:border-blue-500/50 bg-white dark:bg-slate-900 p-6 transition-all shadow-sm hover:shadow-lg hover:shadow-blue-900/10 overflow-hidden">
            {/* Header + inline rename / actions */}
            <div className="flex justify-between items-start mb-4 min-h-12">
                {isEditing ? (
                    <div className="flex gap-2 w-full pr-2">
                        <input
                            autoFocus
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') handleSave();
                                if (e.key === 'Escape') setIsEditing(false);
                            }}
                            className="flex-1 rounded border border-blue-500 bg-white dark:bg-slate-950 px-2 py-1 text-sm text-slate-900 dark:text-white outline-none"
                        />
                        <select
                            value={editCurrency}
                            onChange={(e) =>
                                setEditCurrency(e.target.value as CurrencyCode)
                            }
                            className="rounded border border-blue-500 bg-white dark:bg-slate-950 px-2 py-1 text-sm text-slate-900 dark:text-white outline-none cursor-pointer"
                            title="Reporting base currency"
                        >
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                            <option value="GBP">GBP</option>
                        </select>
                        <button
                            onClick={handleSave}
                            className="rounded bg-emerald-600 px-3 text-xs font-medium text-white transition hover:bg-emerald-500"
                        >
                            Save
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="min-w-0">
                            <h3
                                className="truncate text-lg font-bold text-slate-900 dark:text-white transition-colors group-hover:text-blue-600 dark:group-hover:text-blue-400 cursor-pointer"
                                onClick={goToDetail}
                            >
                                {portfolio.name}
                            </h3>
                            <p className="text-xs text-slate-500">
                                <span className="mr-2 inline-block rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                                    {portfolio.base_currency}
                                </span>
                                Created:{' '}
                                {portfolio.created_at
                                    ? new Date(portfolio.created_at).toLocaleDateString()
                                    : '—'}
                            </p>
                        </div>
                        <div className="flex gap-3 pl-2 opacity-0 transition-opacity group-hover:opacity-100">
                            <button
                                onClick={() => {
                                    setEditName(portfolio.name);
                                    setEditCurrency(
                                        (portfolio.base_currency as CurrencyCode) ||
                                            'USD',
                                    );
                                    setIsEditing(true);
                                }}
                                className="text-xs font-medium text-slate-400 hover:text-blue-500"
                            >
                                Edit
                            </button>
                            <button
                                onClick={() => onDelete(portfolio.id)}
                                className="text-xs font-medium text-slate-400 hover:text-red-500"
                            >
                                Delete
                            </button>
                        </div>
                    </>
                )}
            </div>

            {/* KPI grid — skeleton while the backend crunches, data after */}
            <div className="mt-auto border-t border-slate-100 dark:border-slate-800 pt-4">
                {isLoading ? (
                    <div className="grid animate-pulse grid-cols-2 gap-4">
                        <div>
                            <div className="mb-2 h-3 w-16 rounded bg-slate-200 dark:bg-slate-800" />
                            <div className="h-5 w-24 rounded bg-slate-200 dark:bg-slate-700" />
                        </div>
                        <div>
                            <div className="mb-2 h-3 w-16 rounded bg-slate-200 dark:bg-slate-800" />
                            <div className="h-5 w-20 rounded bg-slate-200 dark:bg-slate-700" />
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                                Unrealized P&amp;L
                            </p>
                            <p
                                className={`font-mono text-sm font-medium ${
                                    unrealized >= 0
                                        ? 'text-emerald-600 dark:text-emerald-400'
                                        : 'text-red-600 dark:text-red-400'
                                }`}
                            >
                                {formatSignedMoney(unrealized, portfolio.base_currency)}
                            </p>
                        </div>
                        <div>
                            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                                YTD vs S&amp;P 500
                            </p>
                            <p className="font-mono text-sm font-medium">
                                <span
                                    className={
                                        ytd >= 0
                                            ? 'text-emerald-600 dark:text-emerald-400'
                                            : 'text-red-600 dark:text-red-400'
                                    }
                                >
                                    {ytd.toFixed(2)}%
                                </span>
                                <span className="mx-1 text-slate-400 dark:text-slate-600">
                                    |
                                </span>
                                <span className="text-slate-500 dark:text-slate-400">
                                    {benchYtd.toFixed(2)}%
                                </span>
                            </p>
                        </div>
                    </div>
                )}
            </div>

            <button
                onClick={goToDetail}
                className="mt-6 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-100/70 dark:bg-slate-800/50 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-300 transition-colors group-hover:border-blue-500 group-hover:bg-blue-600 group-hover:text-white"
            >
                View Analytics Dashboard
            </button>
        </div>
    );
};

// --- Main Hub Page -------------------------------------------------------
export const MyPortfoliosPage = () => {
    const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [newPortName, setNewPortName] = useState('');
    const [newPortCurrency, setNewPortCurrency] = useState<CurrencyCode>('USD');
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadPortfolios = async () => {
        try {
            const data = await fetchPortfolios();
            setPortfolios(data);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to load portfolios', err);
            setError('Could not load your portfolios. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadPortfolios();
    }, []);

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        const name = newPortName.trim();
        if (!name || portfolios.length >= MAX_PORTFOLIOS) return;
        setIsCreating(true);
        setError(null);
        try {
            await createPortfolio(name, newPortCurrency);
            setNewPortName('');
            await loadPortfolios();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            setError('Could not create the portfolio. Please try again.');
        } finally {
            setIsCreating(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (
            !window.confirm(
                'Are you sure? This will permanently delete the asset and all transactions.',
            )
        ) {
            return;
        }
        try {
            await deletePortfolio(id);
            await loadPortfolios();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            setError('Could not delete the portfolio. Please try again.');
        }
    };

    const atLimit = portfolios.length >= MAX_PORTFOLIOS;

    return (
        <Layout
            sidebar={<Sidebar />}
            title="My Portfolios"
            subtitle="Manage up to 6 custom portfolios and track real-time performance."
        >
            {/* Platform-wide asset search — find any symbol and jump to its
                deep-dive page to add it to a portfolio. */}
            <div className="mb-8">
                <GlobalSearchBar />
            </div>

            {/* Toolbar: create form / limit notice */}
            <div className="mb-8 flex flex-col gap-4 border-b border-slate-200 dark:border-slate-800 pb-6 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                    {portfolios.length}/{MAX_PORTFOLIOS} portfolios used.
                </p>

                {atLimit ? (
                    <div className="rounded border border-amber-500/20 bg-amber-500/10 px-4 py-2 text-sm text-amber-600 dark:text-amber-400">
                        Max limit ({MAX_PORTFOLIOS}/{MAX_PORTFOLIOS}) reached.
                    </div>
                ) : (
                    <form
                        onSubmit={handleCreate}
                        className="flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-1.5 shadow-sm transition-colors focus-within:border-blue-500"
                    >
                        <input
                            type="text"
                            placeholder="New Portfolio Name..."
                            value={newPortName}
                            onChange={(e) => setNewPortName(e.target.value)}
                            className="w-48 border-none bg-transparent px-3 py-1.5 text-sm text-slate-900 dark:text-white placeholder-slate-500 outline-none sm:w-64"
                            required
                        />
                        {/* Reporting base currency for the whole portfolio —
                            the lens every aggregate/KPI/TWR is computed in. */}
                        <select
                            value={newPortCurrency}
                            onChange={(e) =>
                                setNewPortCurrency(e.target.value as CurrencyCode)
                            }
                            className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm font-medium text-slate-700 outline-none dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
                            title="Reporting base currency"
                        >
                            <option value="USD">$ USD</option>
                            <option value="EUR">€ EUR</option>
                            <option value="GBP">£ GBP</option>
                        </select>
                        <button
                            type="submit"
                            disabled={isCreating}
                            className="rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50"
                        >
                            {isCreating ? '…' : '+ Create'}
                        </button>
                    </form>
                )}
            </div>

            {error && (
                <div className="mb-6 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-400">
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className="py-16 text-center text-slate-400 animate-pulse">
                    Loading ledgers…
                </div>
            ) : portfolios.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/20 py-32 text-center">
                    <h3 className="mb-2 text-xl font-semibold text-slate-900 dark:text-white">
                        No Portfolios Found
                    </h3>
                    <p className="max-w-sm text-slate-500 dark:text-slate-400">
                        Build up to maximum 6 of your own customized portfolios above to start tracking assets,
                        matching impact of fees, and analyzing P&amp;L.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {portfolios.map((p) => (
                        <PortfolioCard
                            key={p.id}
                            portfolio={p}
                            onDelete={handleDelete}
                            onRefresh={loadPortfolios}
                        />
                    ))}
                </div>
            )}
        </Layout>
    );
};

export default MyPortfoliosPage;
